import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, FileText, MapPin, Calendar, Clock, DollarSign, Truck, User,
  CheckCircle, XCircle, AlertCircle, Loader2, Eye, Navigation, Package,
  Building2, Phone, Mail, Star, Edit, Trash2, X, Send, MoreVertical, TrendingUp, Wallet
} from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { adminSidebarLinks } from '@/config/adminSidebarLinks';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/use-toast';
import { adminAPI } from '@/services/api';
import LogiConnectIdBadge from '@/components/ui/logiconnect-id-badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

const AdminOfferDetails = () => {
  const { transporterId, offerId } = useParams();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(null); // 'accept' or 'reject'
  const [offerData, setOfferData] = useState({
    price: '',
    notes: '',
  });

  // Charger l'offre
  useEffect(() => {
    const fetchOffer = async () => {
      try {
        setLoading(true);
        const response = await adminAPI.getOfferById(offerId);
        setOffer(response.data);
        setOfferData({
          price: response.data.price || '',
          notes: response.data.notes || '',
        });
      } catch (err) {
        console.error('Erreur lors du chargement de l\'offre:', err);
        toast({
          title: 'Erreur',
          description: 'Impossible de charger l\'offre.',
          variant: 'destructive',
        });
        navigate(`/admin/transporters`);
      } finally {
        setLoading(false);
      }
    };

    if (offerId) {
      fetchOffer();
    }
  }, [offerId, transporterId, navigate]);

  // Utilitaires pour les badges
  const getOfferStatusBadge = (status) => {
    const statusConfig = {
      'PENDING': { label: 'En attente', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock },
      'ACCEPTED': { label: 'Acceptée', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
      'REJECTED': { label: 'Rejetée', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
      'EXPIRED': { label: 'Expirée', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', icon: AlertCircle },
    };
    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;
    return (
      <Badge className={config.color}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const getRequestStatusBadge = (status) => {
    const statusConfig = {
      'DRAFT': { label: 'Brouillon', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', icon: FileText },
      'PUBLISHED': { label: 'Publiée', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Send },
      'ASSIGNED': { label: 'Assignée', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
      'IN_TRANSIT': { label: 'En transit', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: Navigation },
      'COMPLETED': { label: 'Terminée', color: 'bg-green-600/20 text-green-400 border-green-600/30', icon: CheckCircle },
      'CANCELLED': { label: 'Annulée', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
    };
    const config = statusConfig[status] || statusConfig.DRAFT;
    const Icon = config.icon;
    return (
      <Badge className={config.color}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}M GNF`;
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(0)}K GNF`;
    }
    return `${amount?.toFixed(0) || 0} GNF`;
  };

  // Calculer le pourcentage de différence entre le prix admin et le prix du transporteur
  const calculatePriceDifference = (adminPrice, transporterPrice) => {
    if (!adminPrice || !transporterPrice || transporterPrice === 0) return null;
    const difference = ((adminPrice - transporterPrice) / transporterPrice) * 100;
    return {
      percentage: Math.abs(difference).toFixed(1),
      isIncrease: difference > 0,
      isDecrease: difference < 0,
      isEqual: difference === 0,
    };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Handlers
  const handleUpdateOffer = async () => {
    try {
      await adminAPI.updateOffer(offerId, {
        price: offerData.price ? parseFloat(offerData.price) : null,
        notes: offerData.notes || '',
      });
      toast({
        title: 'Succès',
        description: 'L\'offre a été mise à jour avec succès.',
      });
      setEditDialogOpen(false);
      // Recharger l'offre
      const response = await adminAPI.getOfferById(offerId);
      setOffer(response.data);
    } catch (err) {
      console.error('Erreur lors de la mise à jour:', err);
      toast({
        title: 'Erreur',
        description: err.response?.data?.detail || 'Impossible de mettre à jour l\'offre.',
        variant: 'destructive',
      });
    }
  };

  const handleActionOffer = async () => {
    if (!actionType) return;

    try {
      if (actionType === 'accept') {
        await adminAPI.forceAcceptOffer(offerId);
        toast({
          title: 'Succès',
          description: 'L\'offre a été acceptée avec succès.',
        });
      } else if (actionType === 'reject') {
        await adminAPI.forceRejectOffer(offerId);
        toast({
          title: 'Succès',
          description: 'L\'offre a été rejetée avec succès.',
        });
      }
      setActionDialogOpen(false);
      setActionType(null);
      // Recharger l'offre
      const response = await adminAPI.getOfferById(offerId);
      setOffer(response.data);
    } catch (err) {
      console.error('Erreur lors de l\'action sur l\'offre:', err);
      toast({
        title: 'Erreur',
        description: err.response?.data?.detail || `Impossible de ${actionType === 'accept' ? 'accepter' : 'rejeter'} l'offre.`,
        variant: 'destructive',
      });
    }
  };


  if (loading) {
    return (
      <DashboardLayout sidebarLinks={adminSidebarLinks}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (!offer) {
    return (
      <DashboardLayout sidebarLinks={adminSidebarLinks}>
        <Card>
          <CardContent className="p-12 text-center">
            <AlertCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Offre non trouvée</h3>
            <p className="text-muted-foreground mb-4">L'offre demandée n'existe pas ou a été supprimée.</p>
            <Button onClick={() => navigate(`/admin/transporters`)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour au profil transporteur
            </Button>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <>
      <Helmet>
        <title>Détails de l'offre - Admin - LogiConnect</title>
        <meta name="description" content="Détails complets de l'offre de transport" />
      </Helmet>
      <DashboardLayout sidebarLinks={adminSidebarLinks}>
        <div className="space-y-6">
          {/* En-tête */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(`/admin/transporters`)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-white">Détails de l'offre</h1>
                <p className="text-muted-foreground mt-1">
                  {offer.logiconnect_id && (
                    <LogiConnectIdBadge id={offer.logiconnect_id} showLabel={true} />
                  )}
                </p>
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  <MoreVertical className="w-4 h-4 mr-2" />
                  Actions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {offer.status === 'PENDING' && (
                  <>
                    <DropdownMenuItem onClick={() => setEditDialogOpen(true)}>
                      <Edit className="w-4 h-4 mr-2" />
                      Modifier le prix
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        setActionType('accept');
                        setActionDialogOpen(true);
                      }}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Forcer l'acceptation
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        setActionType('reject');
                        setActionDialogOpen(true);
                      }}
                      className="text-destructive"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Forcer le rejet
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuItem onClick={() => navigate(`/admin/transporters`)}>
                  <User className="w-4 h-4 mr-2" />
                  Voir le transporteur
                </DropdownMenuItem>
                {offer.request && (
                  <DropdownMenuItem onClick={() => navigate(`/admin/clients/${offer.request.client?.id}/requests/${offer.request.id}`)}>
                    <Package className="w-4 h-4 mr-2" />
                    Voir la demande
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Colonne principale */}
            <div className="lg:col-span-2 space-y-6">
              {/* Section principale - Comparaison des prix mise en évidence */}
              <Card className="bg-gradient-to-br from-primary/20 to-primary/5 border-2 border-primary/30">
                <CardContent className="p-6">
                  <div className="space-y-6">
                    <div className="text-center">
                      <Label className="text-sm text-muted-foreground uppercase tracking-wide mb-4 block">Comparaison des prix</Label>

                      {/* Prix réel du transporteur */}
                      <div className="mb-6">
                        <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Prix réel du transporteur</Label>
                        <div className="text-4xl font-bold text-yellow-400 drop-shadow-lg">
                          {formatCurrency(offer.price)}
                        </div>
                      </div>

                      {/* Prix administrateur */}
                      {offer.admin_display_price ? (
                        <>
                          <div className="flex items-center justify-center gap-4 my-4">
                            <div className="h-px bg-border flex-1"></div>
                            <TrendingUp className="w-5 h-5 text-muted-foreground" />
                            <div className="h-px bg-border flex-1"></div>
                          </div>
                          <div className="mb-4">
                            <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Prix administrateur</Label>
                            <div className="text-4xl font-bold text-yellow-400">
                              {formatCurrency(offer.admin_display_price)}
                            </div>
                            {(() => {
                              const priceDiff = calculatePriceDifference(offer.admin_display_price, offer.price);
                              if (priceDiff && !priceDiff.isEqual) {
                                return (
                                  <div className="flex items-center justify-center gap-2 mt-3">
                                    <Badge className={priceDiff.isIncrease ? 'bg-red-500/20 text-red-400 border-red-500/30 text-lg px-4 py-1' : 'bg-green-500/20 text-green-400 border-green-500/30 text-lg px-4 py-1'}>
                                      {priceDiff.isIncrease ? '↑' : '↓'} {priceDiff.percentage}%
                                    </Badge>
                                    <span className="text-sm text-muted-foreground">
                                      {priceDiff.isIncrease ? 'Augmentation' : 'Réduction'} par rapport au prix réel
                                    </span>
                                  </div>
                                );
                              } else if (priceDiff && priceDiff.isEqual) {
                                return (
                                  <div className="flex items-center justify-center mt-3">
                                    <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30 text-lg px-4 py-1">
                                      = Prix identique
                                    </Badge>
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-sm text-muted-foreground">Aucun prix personnalisé défini</p>
                          <p className="text-xs text-muted-foreground mt-1">Le client verra le prix réel du transporteur</p>
                        </div>
                      )}

                      {/* Prix de la demande du client (référence) */}
                      {(offer.request_detail || offer.request) && (() => {
                        const request = offer.request_detail || offer.request;
                        return request.declared_value ? (
                          <>
                            <div className="flex items-center justify-center gap-4 my-4">
                              <div className="h-px bg-border flex-1"></div>
                              <DollarSign className="w-4 h-4 text-muted-foreground" />
                              <div className="h-px bg-border flex-1"></div>
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Prix déclaré par le client</Label>
                              <div className="text-2xl font-bold text-yellow-400 drop-shadow-lg">
                                {formatCurrency(request.declared_value)}
                              </div>
                              {(() => {
                                const requestPriceDiff = calculatePriceDifference(offer.price, request.declared_value);
                                if (requestPriceDiff && !requestPriceDiff.isEqual) {
                                  return (
                                    <div className="flex items-center justify-center gap-2 mt-2">
                                      <Badge className={requestPriceDiff.isIncrease ? 'bg-orange-500/20 text-orange-400 border-orange-500/30' : 'bg-blue-500/20 text-blue-400 border-blue-500/30'}>
                                        {requestPriceDiff.isIncrease ? '↑' : '↓'} {requestPriceDiff.percentage}%
                                      </Badge>
                                      <span className="text-xs text-muted-foreground">
                                        {requestPriceDiff.isIncrease ? 'Au-dessus' : 'En-dessous'} de la demande client
                                      </span>
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          </>
                        ) : null;
                      })()}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Informations de l'offre */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    Informations de l'offre
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Statut</Label>
                      <div className="mt-1">{getOfferStatusBadge(offer.status)}</div>
                    </div>
                    {offer.final_amount && offer.final_amount !== offer.price && (
                      <div>
                        <Label className="text-muted-foreground">Montant final convenu</Label>
                        <div className="mt-1 font-bold text-2xl text-yellow-400 drop-shadow-lg">{formatCurrency(offer.final_amount)}</div>
                      </div>
                    )}
                  </div>

                  {offer.notes && (
                    <>
                      <Separator />
                      <div>
                        <Label className="text-muted-foreground">Notes</Label>
                        <p className="mt-1 text-sm">{offer.notes}</p>
                      </div>
                    </>
                  )}

                  <Separator />

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Créée le</Label>
                      <p className="mt-1 text-sm">{formatDate(offer.created_at)}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Modifiée le</Label>
                      <p className="mt-1 text-sm">{formatDate(offer.updated_at)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Commission de la plateforme - Affichage pour l'admin */}
              {(offer.request_detail || offer.request) && (() => {
                const request = offer.request_detail || offer.request;
                const offerPrice = parseFloat(offer?.price) || 0;
                let commission = 0;

                if (request?.commission_type === 'PERCENTAGE' && request?.commission_percentage) {
                  commission = offerPrice * (request.commission_percentage / 100);
                } else if (request?.commission_type === 'FIXED_AMOUNT' && request?.commission_fixed_amount) {
                  commission = parseFloat(request.commission_fixed_amount);
                }

                const transporterAmount = offerPrice - commission;

                return commission > 0 ? (
                  <Card className="bg-purple-500/10 border-2 border-purple-500/30">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-purple-400">
                        <Wallet className="w-5 h-5" />
                        Commission de la plateforme
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="text-center py-4">
                        <div className="text-4xl font-bold text-purple-400 mb-2">
                          {formatCurrency(commission)}
                        </div>
                        <p className="text-sm text-purple-300/80">
                          {request.commission_type === 'PERCENTAGE'
                            ? `Commission de ${request.commission_percentage}%`
                            : 'Commission fixe'}
                        </p>
                      </div>
                      <Separator className="bg-purple-500/30" />
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">Prix proposé par le transporteur:</span>
                          <span className="text-sm font-semibold">{formatCurrency(offerPrice)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">Commission plateforme:</span>
                          <span className="text-sm font-semibold text-purple-400">-{formatCurrency(commission)}</span>
                        </div>
                        <Separator className="bg-purple-500/30" />
                        <div className="flex justify-between items-center">
                          <span className="text-base font-semibold">Montant que recevra le transporteur:</span>
                          <span className="text-lg font-bold text-green-400">{formatCurrency(transporterAmount)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : null;
              })()}

              {/* Informations de la demande */}
              {(offer.request_detail || offer.request) && (() => {
                const request = offer.request_detail || offer.request;
                const client = request?.client_detail || request?.client;
                const clientId = client?.id || request?.client;

                return (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Package className="w-5 h-5 text-primary" />
                        Demande associée
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center gap-3">
                        {request.logiconnect_id && (
                          <LogiConnectIdBadge id={request.logiconnect_id} showLabel={true} />
                        )}
                        {getRequestStatusBadge(request.status)}
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-muted-foreground flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            Lieu de départ
                          </Label>
                          <p className="mt-1 font-medium">
                            {request.from_location?.city ||
                             request.from_location?.label ||
                             request.from_location?.address ||
                             'N/A'}
                          </p>
                          {request.from_location?.country && (
                            <p className="text-sm text-muted-foreground">{request.from_location.country}</p>
                          )}
                        </div>
                        <div>
                          <Label className="text-muted-foreground flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            Lieu d'arrivée
                          </Label>
                          <p className="mt-1 font-medium">
                            {request.to_location?.city ||
                             request.to_location?.label ||
                             request.to_location?.address ||
                             'N/A'}
                          </p>
                          {request.to_location?.country && (
                            <p className="text-sm text-muted-foreground">{request.to_location.country}</p>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-muted-foreground">Valeur déclarée</Label>
                          <p className="mt-1 font-bold text-xl text-yellow-400 drop-shadow-lg">{formatCurrency(request.declared_value)}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Urgence</Label>
                          <p className="mt-1">{request.urgency_level || 'NORMAL'}</p>
                        </div>
                      </div>

                      <Accordion type="single" collapsible className="w-full">
                        <AccordionItem value="request-details">
                          <AccordionTrigger className="hover:no-underline">
                            <div className="flex items-center gap-2">
                              <Eye className="w-4 h-4" />
                              Voir les détails de la demande
                            </div>
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="space-y-4 pt-4">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <Label className="text-muted-foreground">Date de création</Label>
                                  <p className="mt-1 text-sm">{formatDate(request.created_at)}</p>
                                </div>
                                <div>
                                  <Label className="text-muted-foreground">Date de modification</Label>
                                  <p className="mt-1 text-sm">{formatDate(request.updated_at)}</p>
                                </div>
                              </div>

                              {request.preferred_pickup_date && (
                                <div>
                                  <Label className="text-muted-foreground">Date de ramassage préférée</Label>
                                  <p className="mt-1 text-sm">{new Date(request.preferred_pickup_date).toLocaleDateString('fr-FR')}</p>
                                </div>
                              )}

                              {request.preferred_delivery_date && (
                                <div>
                                  <Label className="text-muted-foreground">Date de livraison préférée</Label>
                                  <p className="mt-1 text-sm">{new Date(request.preferred_delivery_date).toLocaleDateString('fr-FR')}</p>
                                </div>
                              )}

                              {request.payment_terms && (
                                <div>
                                  <Label className="text-muted-foreground">Conditions de paiement</Label>
                                  <p className="mt-1 text-sm">{request.payment_terms}</p>
                                </div>
                              )}

                              {request.cargo_items && request.cargo_items.length > 0 && (
                                <div>
                                  <Label className="text-muted-foreground">Articles à transporter</Label>
                                  <div className="mt-2 space-y-2">
                                    {request.cargo_items.map((item, index) => (
                                      <div key={index} className="p-2 bg-muted rounded">
                                        <p className="text-sm font-medium">{item.description || 'Article'}</p>
                                        {item.quantity && <p className="text-xs text-muted-foreground">Quantité: {item.quantity}</p>}
                                        {item.weight_kg && <p className="text-xs text-muted-foreground">Poids: {item.weight_kg} kg</p>}
                                        {item.volume_m3 && <p className="text-xs text-muted-foreground">Volume: {item.volume_m3} m³</p>}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              <Button
                                variant="outline"
                                className="w-full"
                                onClick={() => navigate(`/admin/clients/${clientId}/requests/${request.id}`)}
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                Voir la page complète de la demande
                              </Button>
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Informations du véhicule */}
              {(offer.vehicle_detail || offer.vehicle) && (() => {
                const vehicle = offer.vehicle_detail || offer.vehicle;

                return (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Truck className="w-5 h-5 text-primary" />
                        Véhicule proposé
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-muted-foreground">Plaque d'immatriculation</Label>
                          <p className="mt-1 font-semibold">{vehicle.plate_number || vehicle.license_plate || 'N/A'}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Type de véhicule</Label>
                          <p className="mt-1">
                            {vehicle.vehicle_type_label ||
                             vehicle.vehicle_type_description ||
                             vehicle.vehicle_type?.label ||
                             vehicle.vehicle_type?.name ||
                             'N/A'}
                          </p>
                        </div>
                      </div>

                      {(vehicle.make || vehicle.brand) && (
                        <div>
                          <Label className="text-muted-foreground">Marque et modèle</Label>
                          <p className="mt-1">
                            {vehicle.make || vehicle.brand || ''}
                            {vehicle.model ? ` ${vehicle.model}` : ''}
                            {vehicle.year ? ` (${vehicle.year})` : ''}
                          </p>
                        </div>
                      )}

                      {(vehicle.capacity_weight || vehicle.capacity_volume) && (
                        <div className="grid grid-cols-2 gap-4">
                          {vehicle.capacity_weight && (
                            <div>
                              <Label className="text-muted-foreground">Capacité poids</Label>
                              <p className="mt-1 text-sm">{vehicle.capacity_weight} kg</p>
                            </div>
                          )}
                          {vehicle.capacity_volume && (
                            <div>
                              <Label className="text-muted-foreground">Capacité volume</Label>
                              <p className="mt-1 text-sm">{vehicle.capacity_volume} m³</p>
                            </div>
                          )}
                        </div>
                      )}

                      {vehicle.status && (
                        <div>
                          <Label className="text-muted-foreground">Statut</Label>
                          <p className="mt-1 text-sm">{vehicle.status_display || vehicle.status}</p>
                        </div>
                      )}

                      {vehicle.logiconnect_id && (
                        <div>
                          <Label className="text-muted-foreground">ID LogiConnect</Label>
                          <div className="mt-1">
                            <LogiConnectIdBadge id={vehicle.logiconnect_id} showLabel={true} size="sm" />
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })()}
            </div>

            {/* Colonne latérale */}
            <div className="space-y-6">
              {/* Informations transporteur */}
              {(offer.transporter_detail || offer.transporter) && (() => {
                const transporter = offer.transporter_detail || offer.transporter;
                const user = transporter?.user_detail || transporter?.user;

                return (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <User className="w-5 h-5 text-primary" />
                        Transporteur
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label className="text-muted-foreground">Nom</Label>
                        <p className="font-semibold">
                          {transporter.organisation_name ||
                           transporter.company_name ||
                           (user?.first_name && user?.last_name
                             ? `${user.first_name} ${user.last_name}`.trim()
                             : user?.first_name ||
                               user?.email ||
                               'N/A')}
                        </p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          Email
                        </Label>
                        <p className="text-sm">{user?.email || 'N/A'}</p>
                      </div>
                      {user?.phone_number && (
                        <div>
                          <Label className="text-muted-foreground flex items-center gap-2">
                            <Phone className="w-4 h-4" />
                            Téléphone
                          </Label>
                          <p className="text-sm">{user.phone_number}</p>
                        </div>
                      )}
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => navigate(`/admin/transporters`)}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Voir le profil complet
                      </Button>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Informations client */}
              {(() => {
                const request = offer.request_detail || offer.request;
                const client = request?.client_detail || request?.client;
                const user = client?.user_detail || client?.user;

                if (!client) return null;

                return (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-primary" />
                        Client
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label className="text-muted-foreground">Nom</Label>
                        <p className="font-semibold">
                          {client.company_name ||
                           (user?.first_name && user?.last_name
                             ? `${user.first_name} ${user.last_name}`.trim()
                             : user?.first_name ||
                               user?.email ||
                               'N/A')}
                        </p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          Email
                        </Label>
                        <p className="text-sm">{user?.email || 'N/A'}</p>
                      </div>
                      {user?.phone_number && (
                        <div>
                          <Label className="text-muted-foreground flex items-center gap-2">
                            <Phone className="w-4 h-4" />
                            Téléphone
                          </Label>
                          <p className="text-sm">{user.phone_number}</p>
                        </div>
                      )}
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => navigate(`/admin/clients`)}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Voir le profil complet
                      </Button>
                    </CardContent>
                  </Card>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Dialog de modification */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Modifier le prix de l'offre</DialogTitle>
              <DialogDescription>
                Modifiez le prix proposé pour cette offre.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Prix proposé (GNF)</Label>
                <Input
                  type="number"
                  value={offerData.price}
                  onChange={(e) => setOfferData(prev => ({ ...prev, price: e.target.value }))}
                  placeholder="0"
                />
              </div>
              <div>
                <Label>Notes (optionnel)</Label>
                <Input
                  type="text"
                  value={offerData.notes}
                  onChange={(e) => setOfferData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Notes supplémentaires..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleUpdateOffer}>
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog de confirmation d'action */}
        <AlertDialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {actionType === 'accept' ? 'Forcer l\'acceptation de l\'offre' : 'Forcer le rejet de l\'offre'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                Êtes-vous sûr de vouloir {actionType === 'accept' ? 'accepter' : 'rejeter'} cette offre ?
                {actionType === 'accept' && ' Cela créera automatiquement une réservation.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={handleActionOffer}>
                {actionType === 'accept' ? 'Accepter' : 'Rejeter'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DashboardLayout>
    </>
  );
};

export default AdminOfferDetails;
